import React, { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Spinner } from "@material-tailwind/react";
import {
  ArrowDownTrayIcon,
  ArrowLeftIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { portalApi } from "../services/portalApi";
import RevistaPdfNative from "../components/RevistaPdfNative";
import RevistaZineFlipbook from "../components/RevistaZineFlipbook";

/**
 * Lector a pantalla completa (ruta fuera de PortalLayout).
 * - Revista: flipbook zinejs (page-curl + carga progresiva)
 * - PDF: visor nativo del navegador (mejor con archivos muy pesados)
 */
export default function PortalRevistaLector() {
  const [params] = useSearchParams();
  const forcedUrl = (params.get("u") || "").trim();
  const [title, setTitle] = useState("Conexión Siete");
  const [pdfUrl, setPdfUrl] = useState(forcedUrl);
  const [loadingMeta, setLoadingMeta] = useState(!forcedUrl);
  const [error, setError] = useState("");
  // En mobile el flipbook suele recortar; PDF nativo (Fit) se ve completo.
  const [mode, setMode] = useState(() =>
    typeof window !== "undefined" && window.matchMedia("(max-width: 768px)").matches
      ? "pdf"
      : "revista"
  ); // revista | pdf
  const [chromeVisible, setChromeVisible] = useState(true);

  useEffect(() => {
    if (forcedUrl) {
      setPdfUrl(forcedUrl);
      setLoadingMeta(false);
      return undefined;
    }
    let cancelled = false;
    setLoadingMeta(true);
    portalApi
      .revistaCurrent()
      .then((r) => {
        if (cancelled) return;
        const issue = r?.issue;
        if (!issue?.downloadUrl) {
          setError("No hay un ejemplar publicado todavía.");
          return;
        }
        setTitle(issue.title || "Conexión Siete");
        setPdfUrl(issue.downloadUrl);
      })
      .catch((err) => {
        if (!cancelled) setError(err?.message || "No se pudo cargar Conexión Siete");
      })
      .finally(() => {
        if (!cancelled) setLoadingMeta(false);
      });
    return () => {
      cancelled = true;
    };
  }, [forcedUrl]);

  const onFlipFail = useCallback(() => {
    setMode("pdf");
  }, []);

  return (
    <div className="fixed inset-0 z-[100] flex flex-col bg-black">
      {/* Chrome mínimo — se puede ocultar para solo lectura */}
      <div
        className={[
          "absolute top-0 inset-x-0 z-20 transition-transform duration-200",
          chromeVisible ? "translate-y-0" : "-translate-y-full",
        ].join(" ")}
      >
        <div className="flex flex-wrap items-center justify-between gap-2 px-3 sm:px-4 py-2 bg-black/80 backdrop-blur-sm border-b border-white/10">
          <div className="flex items-center gap-2 min-w-0">
            <Link
              to="/revistas"
              className="inline-flex items-center gap-1.5 text-sm text-white/85 hover:text-white shrink-0"
            >
              <ArrowLeftIcon className="h-4 w-4" aria-hidden />
              Salir
            </Link>
            <span className="text-white/25">|</span>
            <h1 className="text-sm text-white truncate font-medium">{title}</h1>
          </div>
          <div className="flex items-center gap-2">
            <div className="inline-flex rounded-md border border-white/20 p-0.5 text-xs">
              <button
                type="button"
                onClick={() => setMode("revista")}
                className={`px-2.5 py-1 rounded ${
                  mode === "revista" ? "bg-white/20 text-white" : "text-white/65 hover:text-white"
                }`}
              >
                Hojeable
              </button>
              <button
                type="button"
                onClick={() => setMode("pdf")}
                className={`px-2.5 py-1 rounded ${
                  mode === "pdf" ? "bg-white/20 text-white" : "text-white/65 hover:text-white"
                }`}
              >
                PDF
              </button>
            </div>
            {pdfUrl ? (
              <a
                href={pdfUrl}
                download
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-xs text-white/80 hover:text-white px-2 py-1.5 rounded border border-white/20"
              >
                <ArrowDownTrayIcon className="h-4 w-4" aria-hidden />
                <span className="hidden sm:inline">Descargar</span>
              </a>
            ) : null}
            <button
              type="button"
              onClick={() => setChromeVisible(false)}
              className="inline-flex h-8 w-8 items-center justify-center rounded border border-white/20 text-white/80 hover:text-white"
              aria-label="Ocultar barra"
              title="Ocultar barra (solo lectura)"
            >
              <XMarkIcon className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      {!chromeVisible ? (
        <button
          type="button"
          onClick={() => setChromeVisible(true)}
          className="absolute top-3 right-3 z-20 text-xs px-2 py-1 rounded bg-black/60 text-white/80 border border-white/20 hover:text-white"
        >
          Menú
        </button>
      ) : null}

      <main className={["flex-1 min-h-0", chromeVisible ? "pt-12" : "pt-0"].join(" ")}>
        {loadingMeta ? (
          <div className="h-full flex items-center justify-center gap-2 text-white/80">
            <Spinner className="h-8 w-8" /> Abriendo…
          </div>
        ) : error ? (
          <div className="h-full flex flex-col items-center justify-center gap-3 px-6 text-center">
            <p className="text-white/90 text-sm">{error}</p>
            <Link to="/revistas" className="text-[#a78bdb] text-sm hover:underline">
              Volver
            </Link>
          </div>
        ) : pdfUrl && mode === "revista" ? (
          <RevistaZineFlipbook pdfUrl={pdfUrl} onFail={onFlipFail} />
        ) : pdfUrl ? (
          <RevistaPdfNative pdfUrl={pdfUrl} title={title} />
        ) : null}
      </main>
    </div>
  );
}
